"use client";

import { useState } from "react";
import Image from "next/image";
import panelDesktop from "@/public/panel-desktop.png";
import panelConsole from "@/public/panel-console.png";
import panelFilemanager from "@/public/panel-filemanager.png";
import panelBackups from "@/public/panel-backups.png";
import panelSchedules from "@/public/panel-schedules.png";
import panelWorldmap from "@/public/panel-worldmap.png";
import panelNbtEditor from "@/public/panel-nbteditor.png";
import { FeatureSection, SectionHeading, BrowserFrame } from "@/app/Components/FeaturePageKit";

/**
 * ── SCREENSHOTS ─────────────────────────────────────────────────────────────
 * Every tab has its own capture from public/. To swap one, drop the new file
 * in public/ and update the import above — nothing else needs changing.
 * Recommended: 1600x1000, PNG or WebP, lowercase extension.
 */
type Shot = typeof panelDesktop;

const tabs: { key: string; label: string; title: string; desc: string; shot: Shot }[] = [
    {
        key: "console",
        label: "Console",
        title: "Live console",
        desc: "Server output as it happens, with a command line that talks straight back.",
        shot: panelConsole,
    },
    {
        key: "files",
        label: "File Manager",
        title: "Full file manager",
        desc: "Upload, move, rename and archive files. Configs open in a real editor.",
        shot: panelFilemanager,
    },
    {
        key: "backups",
        label: "Backups",
        title: "Backups on demand",
        desc: "Snapshot before a risky change, restore when it goes wrong.",
        shot: panelBackups,
    },
    {
        key: "schedules",
        label: "Schedules",
        title: "Scheduled tasks",
        desc: "Restarts, backups and timed commands. Set it once and Spark runs it.",
        shot: panelSchedules,
    },
    {
        key: "worldmap",
        label: "Worldmap",
        title: "Worldmap",
        desc: "Your world as it is on disk, with live player positions and chunk tools.",
        shot: panelWorldmap,
    },
    {
        key: "nbt",
        label: "NBT Editor",
        title: "NBT editor",
        desc: "Edit chunk data directly and inspect it in 3D, with a one-click backup next to it.",
        shot: panelNbtEditor,
    },
];

export default function PanelTabs() {
    const [active, setActive] = useState(tabs[0].key);
    const current = tabs.find((t) => t.key === active) ?? tabs[0];

    return (
        <FeatureSection id="tour">
            <SectionHeading
                eyebrow="Take a look"
                title="A quick tour of"
                accent="Aurex Spark"
                lede="Pick a screen to see what managing your server actually looks like."
            />

            <div className="mt-10 flex flex-wrap gap-2" role="tablist" aria-label="Aurex Spark panel screens">
                {tabs.map((t) => {
                    const isActive = t.key === active;
                    return (
                        <button
                            key={t.key}
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => setActive(t.key)}
                            className={`rounded-full px-4 py-2 text-xs sm:text-sm font-medium font-poppins transition-all ${
                                isActive ? "text-white shadow-sm" : "text-gray-400 bg-white/5 hover:bg-white/10 hover:text-white"
                            }`}
                            style={isActive ? { background: "linear-gradient(90deg, #6416a3 0%, #7c3aed 100%)" } : undefined}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="mt-8 flex flex-col lg:flex-row gap-8 items-start">
                <div className="w-full lg:w-2/3">
                    <BrowserFrame url={`https://panel.aurex.lk/${current.key}`}>
                        <Image
                            key={current.key}
                            src={current.shot}
                            alt={`${current.title} in the Aurex Spark Game Panel`}
                            className="w-full block h-auto"
                        />
                    </BrowserFrame>
                </div>
                <div className="w-full lg:w-1/3 lg:pt-4">
                    <h3 className="text-white font-akira text-lg mb-3">{current.title}</h3>
                    <p className="text-gray-400 font-poppins text-sm leading-relaxed">{current.desc}</p>
                </div>
            </div>
        </FeatureSection>
    );
}
